import {useMemo, useState} from 'react'
import {useDispatch} from 'react-redux'
import {get, map} from '@cullylarson/f'
import Link from 'next/link'
import {preventDefault} from '@@client/lib/events'
import {formatPhone} from '@@client/lib/util'
import {useTokenCache} from '@@client/lib/hooks'
import Messages from '@@client/components/Messages'
import FormText from '@@client/components/FormText'
import FormPhone from '@@client/components/FormPhone'
import FormEmail from '@@client/components/FormEmail'
import StepSummaryItem from './StepSummaryItem'
import NextStepButton from './NextStepButton'
import {checkGuestEmail, clearContactInfoErrors} from './actions'

function GoBackToLoginButton({children, setDesiredStep}) {
    const dispatch = useDispatch()

    const handleClick = preventDefault(() => {
        setDesiredStep('login')
        dispatch(clearContactInfoErrors())
    })

    return <button className="m--as-link" onClick={handleClick}>{children}</button>
}

export default function StepGuest({
    contactName = '',
    contactEmail = '',
    contactPhone = '',
    showSummary,
    onSubmit,
    setDesiredStep,
    disabled = false,
    errors = [],
    paramErrors: initialParamErrors = {},
}) {
    const dispatch = useDispatch()
    const tokenCache = useTokenCache()
    const [name, setName] = useState(contactName)
    const [nameErrors, setNameErrors] = useState([])
    const [email, setEmail] = useState(contactEmail)
    const [emailErrors, setEmailErrors] = useState([])
    const [phone, setPhone] = useState(contactPhone)
    const [phoneErrors, setPhoneErrors] = useState([])

    const isUserError = useMemo(() => <span>An account exists with this email address. Please <GoBackToLoginButton setDesiredStep={setDesiredStep}>login</GoBackToLoginButton> to continue checking out.</span>, [])

    // replace the `is-user` error with a better message
    const paramErrors = useMemo(() => {
        return map((errors) => {
            return map((error) => {
                return {
                    ...error,
                    message: error.code === 'is-user' ? isUserError : error.message,
                }
            }, errors)
        }, initialParamErrors)
    }, [initialParamErrors])

    const idPrefix = 'checkout-guest'

    const handleParam = setParam => e => (e && e.target) ? setParam(e.target.value) : setParam(e)

    const handleSubmit = preventDefault(() => {
        let foundErrors = false

        if(!name) {
            setNameErrors(['Please provide your name.'])
            foundErrors = true
        }
        else {
            setNameErrors([])
        }

        if(!email) {
            setEmailErrors(['Please provide an email address.'])
            foundErrors = true
        }
        else {
            setEmailErrors([])
        }

        if(!phone) {
            setPhoneErrors(['Please provide a phone number.'])
            foundErrors = true
        }
        else {
            setPhoneErrors([])
        }

        if(foundErrors) return

        dispatch(checkGuestEmail(tokenCache, email))
            .then(({success, accountExists}) => {
                if(success && !accountExists) {
                    onSubmit({contactName: name, contactEmail: email, contactPhone: phone})
                }
                else if(accountExists) {
                    setEmailErrors([<span>An account exists with this email address. Please <GoBackToLoginButton setDesiredStep={setDesiredStep}>login</GoBackToLoginButton> to continue checking out.</span>])
                    foundErrors = true
                }
                else if(!success) {
                    setEmailErrors([<span>Something went wrong and your email address could not be verified. Please try again. <Link href='/contact'><a>Contact us</a></Link> if the problem continues.</span>])
                }
            })
    })

    if(showSummary) {
        return (
            <div>
                <StepSummaryItem title='Contact Name'>{contactName}</StepSummaryItem>
                <StepSummaryItem title='Contact Email'>{contactEmail}</StepSummaryItem>
                <StepSummaryItem title='Contact Phone'>{formatPhone(contactPhone)}</StepSummaryItem>
            </div>
        )
    }

    else {
        return (
            <div className='step-guest'>
                <Messages error={errors} />

                <p>Since you are checking out as a guest, we need a few pieces of contact information from you.</p>

                <form onSubmit={handleSubmit}>
                    <div className='fields-wrapper'>
                        <FormText
                            idPrefix={idPrefix}
                            name='name'
                            title='Contact Name'
                            value={name}
                            onChange={handleParam(setName)}
                            errors={nameErrors.concat(get('contactName', [], paramErrors))}
                            required
                            autoComplete='name'
                        />
                        <FormEmail
                            idPrefix={idPrefix}
                            name='email'
                            title='Contact Email'
                            value={email}
                            onChange={handleParam(setEmail)}
                            errors={emailErrors.concat(get('contactEmail', [], paramErrors))}
                            required
                            autoComplete='email'
                        />
                        <FormPhone
                            idPrefix={idPrefix}
                            name='phone'
                            title='Contact Phone'
                            value={phone}
                            onChange={handleParam(setPhone)}
                            errors={phoneErrors.concat(get('contactPhone', [], paramErrors))}
                            required
                            autoComplete='tel'
                        />
                    </div>

                    <NextStepButton disabled={disabled}>Next: Shipping Info</NextStepButton>
                </form>

                <style jsx>
                    {`
                        .fields-wrapper {
                            max-width: 350px;
                            margin: 0 auto;
                        }
                    `}
                </style>
            </div>
        )
    }
}
